import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchPurchaseinvoices,
    createPurchaseInvoice,
    updatePurchaseInvoice,
    deletePurchaseInvoice,
    setPagination,
    setSort,
    searchPurchaseinvoices,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/purchaseinvoice/purchaseinvoiceActions';
import CloudActionButtons, { isContactCloudLocked } from '../../components/pageModule/Customers/CloudActionButtons';
import { useHasPermission } from '../../utils/permissionUtils';
import purchaseinvoiceService from '../../services/purchaseinvoiceService';

const MySwal = withReactContent(Swal);

const Purchaseinvoices = ({ excludedActions = [], onSelectedRowsChange = () => { } }) => {

    const hasViewPermission = useHasPermission(['view purchase invoices']);
    const hasCreatePermission = useHasPermission(['create purchase invoices']);
    const hasUpdatePermission = useHasPermission(['update purchase invoices']);

    const purchaseinvoiceState = useSelector(state => state.purchaseinvoice);
    const dispatch = useDispatch();
    const [cloudLoadingByInvoice, setCloudLoadingByInvoice] = useState({});

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createPurchaseInvoice(formData));
            toast.success('PurchaseInvoice created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating purchaseinvoice', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updatePurchaseInvoice(id, formData));
            toast.success('PurchaseInvoice updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating purchaseinvoice', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deletePurchaseInvoice(id));
                toast.success('PurchaseInvoice deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting purchaseinvoice', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleCloudPush = useCallback(async (row) => {
        if (!row?.id) {
            return;
        }

        if (isContactCloudLocked(row?.cloud)) {
            toast.warning('Purchase invoice cloud sync is locked', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        if (!row?.is_posted) {
            toast.warning('Purchase invoice must be posted before cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        setCloudLoadingByInvoice(prev => ({ ...prev, [row.id]: true }));
        try {
            const response = await purchaseinvoiceService.pushCloud(row.id, {});
            toast.success(response.message || 'Purchase invoice queued for cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            await dispatch(fetchPurchaseinvoices({}, {}, true));
        } catch (error) {
            toast.error(error.message || 'Error pushing purchase invoice to cloud', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setCloudLoadingByInvoice(prev => ({ ...prev, [row.id]: false }));
        }
    }, [dispatch]);

    const columns = useMemo(() => [
        {
            id: 'invoice_number',
            name: 'Invoice Number',
            selector: row => row.invoice_number,
            grow: 2,
        },
        {
            id: 'supplier',
            name: 'Supplier',
            selector: row => row.supplier_name,
            sortField: 'suppliers.name',
            sortable: true,
            grow: 2,
        },
        {
            id: 'total_cost',
            name: 'Total Cost',
            selector: row => row.total_cost,
        },
        {
            id: 'post_status',
            name: 'Post Status',
            selector: row => row.is_posted ?
                <span className="badge bg-success">Posted</span> :
                <span className="badge bg-danger">Unposted</span>,
            sortField: 'purchase_invoices.is_posted',
            sortable: true,
        },
        // {
        //     id: 'cloud',
        //     name: 'Cloud',
        //     minWidth: '140px',
        //     cell: (row) => (
        //         <CloudActionButtons
        //             row={row}
        //             onPush={handleCloudPush}
        //             isPushing={!!cloudLoadingByInvoice[row?.id]}
        //         />
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        // },
    ], [handleCloudPush, cloudLoadingByInvoice]);

    const actions = {
        fetch: (queryFilter, activeFilter) => dispatch(fetchPurchaseinvoices(queryFilter, activeFilter)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchPurchaseinvoices(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows))
    };

    let defaultAvailableActions = ['search', 'filter'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Purchase Invoice"
                moduleName="PurchaseInvoice"
                moduleKey="purchaseinvoices"
                state={purchaseinvoiceState}
                actions={actions}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                urlKey="purchase_invoices"
                actionType="url"
                onSelectedRowsChange={onSelectedRowsChange}
            />
        </>
    );
};

export default Purchaseinvoices;
